import React from 'react';
import { DetailedHTMLPropsWithTestId } from 'app/components/Core/ChakraAutocomplete/types';
import { chakraComponents } from 'chakra-react-select';
import { ChakraAutocomplete } from 'app/components/Core';
import { ReleaseVersionAudioData } from 'types/releases';
import { DisabledAudios } from 'types/general.types';
import { AudioValidationErrorCodes } from 'app/components/Audio';
import { PreferredAudioSelectionOption } from './types';
import { AudioVersionOption } from './AudioVersionOption';
import { getAudioOptions } from './utils';

interface AudioVersionSelectionProps {
  audioData: ReleaseVersionAudioData;
  songTitle: string;
  disabled?: boolean;
  onSelect: (audioGuid: string) => void;
  value: PreferredAudioSelectionOption | null;
  versionSongGuid: string;
  loading: boolean;
  disabledAudios?: DisabledAudios;
  handleInvalidAudioSelect?: (errorCode?: AudioValidationErrorCodes) => void;
}

export const AudioVersionSelection: React.FC<AudioVersionSelectionProps> = ({
  audioData,
  songTitle,
  disabled,
  onSelect,
  value,
  versionSongGuid,
  loading,
  disabledAudios,
  handleInvalidAudioSelect,
}) => {
  return (
    <ChakraAutocomplete<PreferredAudioSelectionOption>
      value={value}
      isLoading={loading}
      getOptionLabel={(option) => option?.title || ''}
      isDisabled={disabled}
      options={getAudioOptions(audioData, disabledAudios)}
      dataTestId="audio-version-field"
      isOptionDisabled={(option) =>
        !!option?.disabledTooltip || !option.isValid
      }
      components={{
        Option: ({ children, ...props }) => {
          const { innerProps, data } = props;
          const invalidAudio =
            data.audioData.validationResult &&
            !data.audioData.validationResult?.isValid;

          return (
            <chakraComponents.Option
              {...props}
              innerProps={
                {
                  ...innerProps,
                  disabled,
                  'data-testid': `audio-version-field-${data.title}-option`,
                  onClick:
                    invalidAudio && handleInvalidAudioSelect
                      ? () =>
                          handleInvalidAudioSelect(
                            data.audioData.validationResult?.errors[0].errorCode
                          )
                      : innerProps.onClick,
                } as DetailedHTMLPropsWithTestId
              }
            >
              <AudioVersionOption
                option={data as PreferredAudioSelectionOption}
                songTitle={songTitle}
                versionSongGuid={versionSongGuid}
                tooltip={data?.disabledTooltip}
              />
            </chakraComponents.Option>
          );
        },
      }}
      onChange={(option) => {
        if (option) {
          onSelect(option.value.toString());
        }
      }}
    />
  );
};
